import React from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Card,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Link } from 'react-router-dom';
import { ActionItem, ReasoningStep } from '../onboardingIntelligence.types';
import { pushToLive } from '../services/OnboardingIntelligenceService';
import { useMountedRef } from '../hooks/useMountedRef';
import {
    CARD_SX,
    HEADING_NAVY,
    PILL_BUTTON_SX,
    SCENARIO_ACCENT,
    SECTION_LABEL_SX,
} from './onboardingIntelligence.styles';

type Scenario1Item = Extract<ActionItem, { kind: 'scenario1' }>;
type Scenario2Item = Extract<ActionItem, { kind: 'scenario2' }>;
type Scenario3Item = Extract<ActionItem, { kind: 'scenario3' }>;

interface ReasoningListProps {
    steps: ReasoningStep[];
}

const ReasoningList: React.FC<ReasoningListProps> = ({ steps }) => (
    <Accordion
        elevation={0}
        sx={{
            borderTop: '1px solid rgba(0,0,0,0.08)',
            backgroundColor: 'transparent',
            '&::before': { display: 'none' },
        }}
        data-testid='onboarding-intelligence-action-reasoning'
    >
        <Tooltip title='Show me the tasks you ran' placement='left'>
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-label='Show me the tasks you ran'
                sx={{
                    padding: 0,
                    minHeight: '28px',
                    '& .MuiAccordionSummary-content': { margin: 0 },
                }}
            />
        </Tooltip>
        <AccordionDetails sx={{ padding: '0 0 8px 0' }}>
            <Stack spacing={0.5}>
                {steps.map((s, i) => (
                    <Box key={`${s.label}-${i}`}>
                        <Typography
                            variant='caption'
                            component='span'
                            sx={{ fontStyle: 'italic', color: 'rgba(0,0,0,0.7)' }}
                        >
                            {s.label}:
                        </Typography>{' '}
                        <Typography variant='caption' component='span'>
                            {s.result}
                        </Typography>
                    </Box>
                ))}
            </Stack>
        </AccordionDetails>
    </Accordion>
);

interface CardShellProps {
    accent: string;
    title: React.ReactNode;
    cta: React.ReactNode;
    reasoning: ReasoningStep[];
    testId: string;
}

const ActionItemCardShell: React.FC<CardShellProps> = ({
    accent,
    title,
    cta,
    reasoning,
    testId,
}) => (
    <Box
        sx={{
            paddingTop: '12px',
            paddingBottom: '12px',
            borderBottom: '1px solid rgba(0,0,0,0.06)',
            '&:last-of-type': { borderBottom: 'none' },
        }}
        data-testid={testId}
    >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
            <Box
                aria-hidden
                sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: accent,
                    marginTop: '7px',
                    flexShrink: 0,
                }}
            />
            <Box sx={{ flex: 1 }}>
                <Typography variant='body2' sx={{ color: HEADING_NAVY, lineHeight: 1.45 }}>
                    {title}
                </Typography>
                <Box sx={{ marginTop: '10px' }}>{cta}</Box>
            </Box>
        </Box>
        <Box sx={{ marginTop: '6px' }}>
            <ReasoningList steps={reasoning} />
        </Box>
    </Box>
);

const Scenario1Card: React.FC<{ item: Scenario1Item }> = ({ item }) => {
    const { supplier } = item;
    return (
        <ActionItemCardShell
            testId='onboarding-intelligence-action-card-scenario1'
            accent={SCENARIO_ACCENT.scenario1}
            title={
                <>
                    <strong>{supplier.name}</strong> — {supplier.days} days with no{' '}
                    {supplier.incompleteTask || 'progress'}. Escalate immediately.
                </>
            }
            cta={
                <Button
                    variant='outlined'
                    size='small'
                    component='a'
                    href={`mailto:${supplier.email}`}
                    sx={PILL_BUTTON_SX}
                    data-testid='onboarding-intelligence-action-card-scenario1-cta'
                >
                    {supplier.email}
                </Button>
            }
            reasoning={item.reasoning}
        />
    );
};

interface Scenario2Props {
    item: Scenario2Item;
    onAction: () => void;
    busy: boolean;
}

const Scenario2Card: React.FC<Scenario2Props> = ({ item, onAction, busy }) => {
    const { supplier } = item;
    return (
        <ActionItemCardShell
            testId='onboarding-intelligence-action-card-scenario2'
            accent={SCENARIO_ACCENT.scenario2}
            title={
                <>
                    <strong>{supplier.name}</strong> — Fully Ready and{' '}
                    {supplier.status} status.
                    <br />
                    Push live today.
                </>
            }
            cta={
                <Button
                    variant='outlined'
                    size='small'
                    onClick={onAction}
                    disabled={busy}
                    aria-busy={busy || undefined}
                    sx={PILL_BUTTON_SX}
                    data-testid='onboarding-intelligence-action-card-scenario2-cta'
                >
                    Push Suppliers to Production
                </Button>
            }
            reasoning={item.reasoning}
        />
    );
};

const Scenario3Card: React.FC<{ item: Scenario3Item }> = ({ item }) => {
    const { supplier } = item;
    return (
        <ActionItemCardShell
            testId='onboarding-intelligence-action-card-scenario3'
            accent={SCENARIO_ACCENT.scenario3}
            title={
                <>
                    <strong>{supplier.name}</strong> has completed the onboarding form{' '}
                    {supplier.daysAgo === 0
                        ? 'today'
                        : `${supplier.daysAgo} day${supplier.daysAgo === 1 ? '' : 's'} ago`}
                    .
                </>
            }
            cta={
                <Button
                    variant='outlined'
                    size='small'
                    component={Link}
                    to={`/onboarding/integration?id=${supplier.id}`}
                    data-testid='onboarding-intelligence-action-card-scenario3-cta'
                >
                    Review Form
                </Button>
            }
            reasoning={item.reasoning}
        />
    );
};

interface Props {
    actionItems: ActionItem[];
    onRefresh: () => void;
    unevaluated: number;
}

const ActionItemsColumn: React.FC<Props> = ({ actionItems, onRefresh, unevaluated }) => {
    const [busyId, setBusyId] = React.useState<string | null>(null);
    const [actionError, setActionError] = React.useState<string | null>(null);
    const mountedRef = useMountedRef();

    const handlePush = async (id: string) => {
        setBusyId(id);
        setActionError(null);
        try {
            await pushToLive(id);
            if (mountedRef.current) onRefresh();
        } catch (err) {
            if (mountedRef.current) {
                setActionError(
                    err instanceof Error
                        ? err.message
                        : 'Could not push supplier to production'
                );
            }
        } finally {
            if (mountedRef.current) setBusyId(null);
        }
    };

    const renderItem = (item: ActionItem) => {
        switch (item.kind) {
            case 'scenario1':
                return <Scenario1Card key={item.supplier.id} item={item} />;
            case 'scenario2':
                return (
                    <Scenario2Card
                        key={item.supplier.id}
                        item={item}
                        busy={busyId === item.supplier.id}
                        onAction={() => handlePush(item.supplier.id)}
                    />
                );
            case 'scenario3':
                return <Scenario3Card key={item.supplier.id} item={item} />;
            default: {
                const _exhaustive: never = item;
                return _exhaustive;
            }
        }
    };

    return (
        <Card
            data-testid='onboarding-intelligence-action-items'
            sx={{
                ...CARD_SX,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    padding: '16px 20px 0 20px',
                }}
            >
                <Typography variant='subtitle2' sx={SECTION_LABEL_SX}>
                    Action Items ({actionItems.length})
                </Typography>
                {unevaluated > 0 && (
                    <Typography
                        variant='caption'
                        sx={{ color: 'rgba(0,0,0,0.5)' }}
                        data-testid='onboarding-intelligence-unevaluated'
                    >
                        {unevaluated} couldn&rsquo;t be evaluated
                    </Typography>
                )}
            </Box>
            {actionError && (
                <Alert severity='error' sx={{ margin: '12px 20px 0 20px' }}>
                    {actionError}
                </Alert>
            )}
            <Box
                sx={{
                    flex: 1,
                    maxHeight: 480,
                    overflowY: 'auto',
                    padding: '0 20px 8px 20px',
                }}
            >
                {actionItems.length === 0 ? (
                    <Typography
                        variant='body2'
                        sx={{ color: 'rgba(0,0,0,0.6)', padding: '12px 0' }}
                    >
                        ✓ No urgent actions right now
                    </Typography>
                ) : (
                    actionItems.map(renderItem)
                )}
            </Box>
        </Card>
    );
};

export default ActionItemsColumn;
